// @flow
import createAction from 'Store/utils/createAction';
import {
  CUSTOMER_ACCOUNT_REMOVE_BOOKING_CART_ITEM,
  CUSTOMER_ACCOUNT_UPDATE_BOOKING_CART,
  CUSTOMER_ACCOUNT_GET_BOOKING_CART,
} from 'Utils/request/requestDictionary';
import graphQLClient from 'Utils/graphql-client/graphqlClient';
import { attributesToString } from 'Utils/graphql-client/graphqlClient.utils';

const FETCH_BOOKING_CART = 'FETCH_BOOKING_CART';
export const UPDATE_BOOKING_CART = 'UPDATE_BOOKING_CART';

const cartQuery = `
    cart {
        childcares {
            id
            productId
            label
            type
            attendees {
                id
                price
            }
            startDate
            endDate
            schedules {
              startDate
              endDate
              attendees {
                id
                price
              }
            }
        }
        transfers {
          id
          productId
          type
          label
          schedules {
            startDate
            endDate
            attendees {
              id
              price
            }
          }
        }
        rentals {
          id
          productId
          type
          schedules {
            startDate
            endDate
            attendees {
              id
              price
            }
          }
          attendees {
            id
            schedules {
              price {
                value
              }
              startDate
              endDate
            }
          }
        }
        activities {
          id
          productId
          type
          schedules {
            startDate
            endDate
            attendees {
              id
              price
            }
          }
        }
        pickRoom { 
          id
          type
          accommodations {
            id 
            rooms {
              id
            }
          }
        }
        price {
            total {
                formattedValue(precision: 2)
                value
                currency
            }
            currency
            detail {
              service
              amount
            }
        }
        isEmpty
    }
`;

export const getBookingCartQuery = (bookingId: string) => {
  return `query {
        userBooking(bookingId : "${bookingId}") {
          ${cartQuery}
        }
    }`;
};

export const fetchBookingCart = (stay: Stay, context: AppContext) => (dispatch: ReduxDispatch) =>
  dispatch(
    createAction(
      FETCH_BOOKING_CART,
      { stay },
      {
        optimist: true,
        promise: graphQLClient.runNoCacheQuery(getBookingCartQuery(stay.bookingId), context, {
          requestName: CUSTOMER_ACCOUNT_GET_BOOKING_CART,
        }),
      },
    ),
  );

type mutationSchedules = $ReadOnlyArray<{
  start_date: string,
  end_date: string,
  attendees: $ReadOnlyArray<{|
    id: string,
  |}>,
}>;

type mutationAccommodations = Array<{
  id: string,
  rooms: Array<{
    id: string,
  }>,
}>;

type addServiceParams = {
  id: string,
  bookingId: string,
  schedules?: mutationSchedules,
  accommodations?: mutationAccommodations,
  action?: string,
  serviceName: ServiceType,
};

const addService = ({
  id,
  bookingId,
  schedules,
  accommodations,
  serviceName,
}: addServiceParams) => {
  return `mutation {
            addServiceCart(
             bookingId : "${bookingId}",
             serviceCart: {
                 id: "${id}",
                 ${schedules ? attributesToString({ schedules }) : ''}
                 ${accommodations ? attributesToString({ accommodations }) : ''}
             }
             serviceName: "${serviceName}") {
                ${cartQuery}
             }
          }`;
};

export const removeService = (id: string, bookingId: string) => {
  return `mutation {
              removeServiceCart(
               bookingId : "${bookingId}",
               itemId: "${id}")
               {
                ${cartQuery}
               }
            }`;
};

export type updateCartParams = {
  id: string,
  bookingId: string,
  schedules?: mutationSchedules,
  accommodations?: mutationAccommodations,
  context: AppContext,
  action?: string,
  serviceName: ServiceType,
  successMsg?: string,
};

export const updateBookingCart = ({
  id,
  bookingId,
  schedules,
  accommodations,
  serviceName,
  context,
  action,
  successMsg,
}: updateCartParams) => (dispatch: ReduxDispatch) =>
  dispatch(
    createAction(
      UPDATE_BOOKING_CART,
      { id, toastLabel: successMsg },
      {
        optimist: true,
        promise: graphQLClient.runNoCacheQuery(
          action === 'update'
            ? addService({ id, bookingId, schedules, accommodations, serviceName })
            : removeService(id, bookingId),
          context,
          {
            requestName:
              action === 'update'
                ? CUSTOMER_ACCOUNT_UPDATE_BOOKING_CART
                : CUSTOMER_ACCOUNT_REMOVE_BOOKING_CART_ITEM,
          },
        ),
      },
    ),
  );

export const updateBookingCartSequencially = (params: Array<updateCartParams>) => {
  if (params.length === 0) {
    return Promise.resolve();
  }

  const { id, bookingId, schedules, accommodations, serviceName, context, action } = params[0];

  const promise = graphQLClient.runNoCacheQuery(
    action === 'update'
      ? addService({ id, bookingId, schedules, accommodations, serviceName })
      : removeService(id, bookingId),
    context,
    {
      requestName:
        action === 'update'
          ? CUSTOMER_ACCOUNT_UPDATE_BOOKING_CART
          : CUSTOMER_ACCOUNT_REMOVE_BOOKING_CART_ITEM,
    },
  );

  return promise.then(() => {
    return updateBookingCartSequencially(params.slice(1));
  });
};

export const updateBookingCartAll = (params: Array<updateCartParams>) => {
  return Promise.all(
    params.map(({ id, bookingId, schedules, accommodations, serviceName, context, action }) =>
      graphQLClient.runNoCacheQuery(
        action === 'update'
          ? addService({ id, bookingId, schedules, accommodations, serviceName })
          : removeService(id, bookingId),
        context,
        {
          requestName:
            action === 'update'
              ? CUSTOMER_ACCOUNT_UPDATE_BOOKING_CART
              : CUSTOMER_ACCOUNT_REMOVE_BOOKING_CART_ITEM,
        },
      ),
    ),
  );
};
